import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { Download, ChevronUp, ChevronRight, FileText, RefreshCw } from 'lucide-react';
import { PPIMAPI } from '../../services/api/ppimAPI';
import { PolicyStatusModal } from './partner/PolicyStatusModal';

const DECISION_COLORS = {
  'For Review':      'bg-[#FFA500] text-white',
  'Approved':        'bg-[#2C853C] text-white',
  'Failed':          'bg-[#DC143C] text-white',
  'Excluded':        'bg-[#4169E1] text-white',
  'Suggest Approve': 'bg-[#6D4694] text-white',
  'Suggest Exclude': 'bg-[#6D4694] text-white',
  'Suggest Fail':    'bg-[#6D4694] text-white',
};

const SCORE_COLORS = {
  Pass: 'bg-green-100 text-green-700',
  Fail: 'bg-red-100 text-red-700',
};

const CATEGORY_ORDER = [
  'Purpose & Use',
  'Data Categories',
  'WINR & Verbatim',
  'International Transfers',
  'Data Subject Rights',
  'Data Controller',
  'Supervisory Authority',
  'Policy Update',
];

const QUESTION_CATEGORIES = {
  Q1:  'Purpose & Use',
  Q2:  'Purpose & Use',
  Q3:  'Purpose & Use',
  Q4:  'Purpose & Use',
  Q5:  'Data Categories',
  Q6:  'Data Categories',
  Q7:  'WINR & Verbatim',
  Q8:  'WINR & Verbatim',
  Q9:  'WINR & Verbatim',
  Q10: 'International Transfers',
  Q11: 'International Transfers',
  Q12: 'Data Subject Rights',
  Q13: 'Data Subject Rights',
  Q14: 'Data Subject Rights',
  Q15: 'Data Controller',
  Q16: 'Data Controller',
  Q17: 'Data Controller',
  Q18: 'Supervisory Authority',
  Q19: 'Supervisory Authority',
  Q20: 'Policy Update',
  Q21: 'Policy Update',
};

const QUESTION_TEXTS = {
  Q1:  'Locate the first substantive section of the privacy policy—early in the document—that explains why the organization collects or processes personal data (i.e., the purposes or intended uses of the data)',
  Q2:  'Does the privacy policy state that collected personal data can be shared with partners or third-party providers?',
  Q3:  'Does the privacy policy state that collected personal data can be used for digital advertising or marketing communications?',
  Q4:  'Does the privacy policy state that collected personal data can be used for identity verification or fraud prevention?',
  Q5:  'Does the privacy policy list all the following data categories of personal data, even if they are expressed using synonymous or equivalent terms; full name, gender, date of birth, residential address, email address, telephone number. Count a category as present if it appears in any equivalent wording.',
  Q6:  'Does the policy explicitly state that it collects or processes IP or IP addresses',
  Q7:  'Within the section that relates to trusted partners or third parties is WINR named?',
  Q8:  'Does the privacy policy include the exact WINR data sharing verbatim wording covering fraud prevention and digital advertising purposes?',
  Q9:  'Does the privacy policy include the exact WINR retention and rights verbatim wording covering lawful bases and Data Protection Officer contact?',
  Q10: 'Does the privacy policy explain safeguards under which personal data may be transferred internationally?',
  Q11: 'Does the privacy policy refer to all of the following in relation to international transfers; adequacy decisions, standard contractual clauses and data transfer agreements?',
  Q12: 'Does the privacy policy detail how long personal data may be retained?',
  Q13: 'Does the privacy policy describe the rights of data subjects including; access, correction, deletion, objection, portability.',
  Q14: 'Does the privacy policy explain the right to withdraw consent?',
  Q15: 'Does the privacy policy provide details of the data controller?',
  Q16: 'Is the Data Controller name related to the company name?',
  Q17: 'Within the section naming the data controller, does the privacy policy provide contact details for the Data Protection Officer?',
  Q18: 'Does the privacy policy explain the right to lodge a complaint with a supervisory authority?',
  Q19: 'Ignore all references to GDPR, EU law and EEA law. Identify ONLY references to privacy or data-protection laws, frameworks, or regulatory authorities from outside GDPR jurisdictions (for example: CCPA/CPRA, US state laws, PIPEDA, Australian Privacy Act, Brazilian LGPD, etc.)',
  Q20: 'Does the privacy policy explain how changes to the privacy policy will be communicated?',
  Q21: 'Does the privacy policy display the date of its last update?',
};

/** Normalize raw API response into the shape the UI needs */
const normalizePolicy = (raw) => {
  const categories = CATEGORY_ORDER
    .filter(name => raw.parsed_score?.[name] !== undefined)
    .map(name => ({
      name,
      score: raw.parsed_score[name].weighted_sum,
      total: raw.parsed_score[name].max_possible,
    }));

  const questions = Object.entries(raw.parsed_result ?? {}).map(([id, q]) => ({
    id,
    question: QUESTION_TEXTS[id] ?? id,
    category: QUESTION_CATEGORIES[id] ?? '',
    weight: q.weight,
    score: q.score,
    result: q.passed ? 'Pass' : 'Fail',
    reason: q.reason,
    response: q.response,
    threshold: q.threshold,
    weightedScore: q.weighted_score,
  }));

  return { ...raw, categories, questions };
};

export const PolicyDetail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const policyParam = searchParams.get('policy');
  const ppfFile = searchParams.get('ppf_file');

  const api = PPIMAPI(auth.user?.access_token);

  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [expandedMap, setExpandedMap] = useState({});
  const questionRefs = useRef([]);

  const fetchPolicy = () => {
    if (!policyParam) { setLoading(false); return; }
    setLoading(true);
    api.getPolicyById(policyParam)
      .then(data => {
        const normalized = normalizePolicy(data);
        setPolicy(normalized);
        setExpandedMap({});
      })
      .catch(() => setPolicy(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPolicy(); }, [policyParam]);

  const toggleExpanded = (id) => setExpandedMap(prev => ({ ...prev, [id]: !prev[id] }));

  const scrollToQuestion = (i) => {
    questionRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!policyParam) {
    return (
      <>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Policy Details</h2>
          <p className="text-sm text-gray-500 mt-0.5">View and manage policy assessment details</p>
        </div>
        <div className="border border-gray-200 rounded-xl flex flex-col items-center justify-center py-24">
          <FileText className="w-12 h-12 text-gray-300 mb-4" />
          <p className="text-base font-bold text-gray-900 mb-1">No Policy Selected</p>
          <p className="text-sm text-gray-500">To show a policy's details click on the policy in the Partner Profile section</p>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!policy) {
    return (
      <div className="text-center py-24 text-gray-500">
        <p className="text-lg font-medium">Policy not found</p>
        <p className="text-sm mt-1">{policyParam}</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-blue-600 hover:underline text-sm">
          Go back
        </button>
      </div>
    );
  }

  const passedCount = policy.questions?.filter(q => q.result === 'Pass').length ?? 0;
  const totalCount = policy.questions?.length ?? 0;

  const downloadPDF = () => {
    const nameParts = [ppfFile, policy.checksum].filter(Boolean);
    const title = nameParts.join(' | ');

    const questionsHtml = (policy.questions ?? []).map(q => {
      const resultColor = q.result === 'Pass' ? '#16a34a' : '#dc2626';
      return `
        <div class="question">
          <div class="q-meta">
            <span class="q-id">${q.id}</span>
            <span class="badge">${q.category}</span>
            <span>Weight: ${q.weight}</span>
            <span>Threshold: ${q.threshold}</span>
            <span style="color:${resultColor};font-weight:600">Score: ${q.score} (${q.result})</span>
          </div>
          <p class="q-text">${q.question}</p>
          ${q.reason ? `<p class="q-reason" style="color:${resultColor}"><strong>Reason:</strong> ${q.reason}</p>` : ''}
          ${q.response ? `<p class="q-response"><strong>Response:</strong> ${q.response}</p>` : ''}
        </div>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><title>${title}</title><style>
      body{font-family:Arial,sans-serif;font-size:12px;color:#1a1a1a;margin:0;padding:32px}
      h1{font-size:18px;margin:0 0 4px}
      .subtitle{font-size:11px;color:#555;margin:0 0 6px}
      .meta{font-size:10px;color:#666;margin-bottom:16px}
      hr{border:none;border-top:1px solid #ddd;margin:16px 0}
      h2{font-size:14px;margin:0 0 12px}
      .question{border-bottom:1px solid #eee;padding:10px 0;page-break-inside:avoid}
      .q-meta{display:flex;gap:12px;align-items:center;font-size:10px;color:#666;margin-bottom:4px;flex-wrap:wrap}
      .q-id{font-weight:700;color:#111;font-size:11px}
      .badge{background:#f3f4f6;padding:1px 6px;border-radius:4px}
      .q-text{margin:4px 0;font-weight:600;font-size:12px}
      .q-reason,.q-response{margin:3px 0;font-size:11px;color:#444}
      @media print{body{padding:20px}button{display:none}}
    </style></head><body>
      <h1>Policy Assessment Report</h1>
      <p class="subtitle">${title}</p>
      <p class="meta">Decision: <strong>${policy.decision ?? '—'}</strong> &nbsp;|&nbsp; Status: <strong>${policy.status ?? '—'}</strong> &nbsp;|&nbsp; Final Score: <strong>${policy.final_score ?? '—'}</strong> &nbsp;|&nbsp; AI Version: <strong>${policy.ai_version ?? '—'}</strong> &nbsp;|&nbsp; Assessment Date: <strong>${policy.assessment_date ?? '—'}</strong></p>
      <hr/>
      <h2>Assessment Questions (${passedCount}/${totalCount} passed)</h2>
      ${questionsHtml}
    </body></html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.focus();
    win.print();
  };

  return (
    <>
      {/* Page header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Policy Details</h2>
          <p className="text-sm text-gray-500 mt-0.5">View and manage policy assessment details</p>
        </div>
        <div className="flex items-center gap-3 mt-1">
          <p className="text-sm text-gray-600">
            Checksum: <span className="font-semibold text-gray-900 font-mono text-xs">{policy.checksum}</span>
          </p>
          <button
            onClick={fetchPolicy}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            title="Refresh policy data"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Checksum + Evaluation Details label */}
      <div className="mb-4">
        <p className="text-base font-bold text-gray-900 font-mono text-sm">{ppfFile ?? policy.checksum}</p>
        <p className="text-sm text-gray-500">Evaluation Details</p>
      </div>

      {/* Decision / Status / Score — dark banner */}
      <div className="bg-gray-900 rounded-xl px-6 py-5 mb-4">
        <div className="flex items-center gap-12">
          <div>
            <p className="text-xs font-medium text-gray-400 mb-2">Decision</p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setShowModal(true)}
                className={`px-3 py-1 rounded text-sm font-medium border border-gray-500 hover:opacity-80 transition-opacity ${DECISION_COLORS[policy.decision] ?? 'bg-white text-gray-900'}`}
                title="Click to update decision"
              >
                {policy.decision ?? '—'}
              </button>
              {policy.decision_changed === 1 && <span className="text-red-400 text-sm font-bold">*</span>}
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-400 mb-2">Status</p>
            <span className={`px-3 py-1 rounded text-sm font-bold ${{
              PR1: 'bg-[#2C853C] text-white',
              PR2: 'bg-[#FFA500] text-white',
              PR3: 'bg-[#DC143C] text-white',
            }[policy.status] ?? 'text-white'}`}>{policy.status ?? '—'}</span>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-400 mb-2">Final Score</p>
            <p className="text-lg font-bold text-white">{policy.final_score ?? '—'}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-400 mb-2">AI Version</p>
            <p className="text-lg font-bold text-white">{policy.ai_version ?? '—'}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-400 mb-2">Assessment Date</p>
            <p className="text-lg font-bold text-white">{policy.assessment_date ?? '—'}</p>
          </div>
        </div>
      </div>

      {/* Category score grid */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        {policy.categories?.map(cat => (
          <div key={cat.name} className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs font-medium text-blue-600 mb-2">{cat.name}</p>
            <p className="text-xl font-bold text-gray-900">{cat.score}/{cat.total}</p>
          </div>
        ))}
      </div>

      {/* Q navigation pills */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap gap-2">
          {policy.questions?.map((q, i) => (
            <button
              key={q.id}
              onClick={() => scrollToQuestion(i)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                q.result === 'Pass'
                  ? 'border-green-400 text-green-700 hover:bg-green-50'
                  : 'border-red-400 text-red-700 hover:bg-red-50'
              }`}
            >
              {q.id}
            </button>
          ))}
        </div>
      </div>

      {/* Assessment Questions header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-gray-900">
          Assessment Questions ({passedCount}/{totalCount})
        </h3>
        <button onClick={downloadPDF} className="flex items-center gap-1.5 px-3 py-1.5 text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors">
          <Download className="w-4 h-4" />
          Download PDF
        </button>
      </div>

      {/* Question cards */}
      <div className="space-y-2 pb-8">
        {policy.questions?.map((q, i) => {
          const isExpanded = !!expandedMap[q.id];
          return (
            <div
              key={q.id}
              ref={el => questionRefs.current[i] = el}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden"
            >
              {/* Header row */}
              <button
                className="w-full text-left px-5 py-4 flex items-center justify-between gap-4"
                onClick={() => toggleExpanded(q.id)}
              >
                <div className="flex flex-col gap-2 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm font-bold text-gray-900">{q.id}</span>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{q.category}</span>
                    <span className="text-xs text-gray-500">Weight: {q.weight}</span>
                    <span className="text-xs text-gray-500">Threshold: {q.threshold}</span>
                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${SCORE_COLORS[q.result] ?? 'bg-gray-100 text-gray-600'}`}>
                      Score: {q.score} ({q.result})
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">{q.question}</p>
                </div>
                {isExpanded
                  ? <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" />
                  : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                }
              </button>

              {/* Expandable detail */}
              {isExpanded && (
                <div className="border-t border-gray-100">
                  <div className="px-5 py-4 space-y-4">
                    {q.reason && (
                      <div>
                        <p className="text-sm font-semibold text-gray-900 mb-1">Reason</p>
                        <p className="text-sm text-blue-600">{q.reason}</p>
                      </div>
                    )}
                    {q.response && (
                      <div>
                        <p className="text-sm font-semibold text-gray-900 mb-1">Response</p>
                        <p className="text-sm text-gray-600 whitespace-pre-line">{q.response}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showModal && (
        <PolicyStatusModal
          policy={{ ...policy, ppf_file_name: ppfFile }}
          onClose={() => setShowModal(false)}
          onFetchHistory={(ppfId) => api.getDecisionHistory(ppfId)}
          onUpdate={async (_id, changes) => {
            const username = auth.user?.profile?.name ?? auth.user?.profile?.email ?? null;
            const result = await api.updatePolicyStatus(policy.ppf_id, { ...changes, requested_by: username, approved_by: username });
            setPolicy(prev => ({ ...prev, decision: result.decision, decision_changed: result.decision_changed }));
            setShowModal(false);
          }}
        />
      )}
    </>
  );
};
